import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { MIN_PASSWORD_LENGTH } from '@careapp-group/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import * as checkStrength from 'zxcvbn';

@Component({
  selector: 'ca-password-input',
  styleUrls: ['./password-input.component.scss'],
  templateUrl: './password-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CaPasswordInputComponent),
    },
  ],
})
export class CaPasswordInputComponent implements OnInit, ControlValueAccessor, OnDestroy {
  public minLength = MIN_PASSWORD_LENGTH;
  public score$ = new BehaviorSubject<number>(null);
  public form: UntypedFormGroup;
  showPassword = false;
  showConfirmPassword = false;

  changes: Subscription;

  @Input()
  public value: string;

  @Input()
  public onLightBackground: boolean = false;

  @Output()
  public isValid = new EventEmitter<boolean>();

  onChange: (value: string) => void;
  onTouched: () => void;
  onValidatorChange: () => void;

  constructor(public store: Store<any>, private formBuilder: UntypedFormBuilder) {}

  ngOnInit() {
    this.form = this.formBuilder.group(
      {
        newPassword: this.formBuilder.control('', [
          Validators.required,
          Validators.minLength(MIN_PASSWORD_LENGTH),
        ]),
        confirmPassword: this.formBuilder.control('', [Validators.required]),
      },
      {
        validators: [
          (control: AbstractControl) =>
            (control.get('newPassword').value?.length ?? 0) < MIN_PASSWORD_LENGTH
              ? { minLength: true }
              : null,
          (control: AbstractControl) =>
            control.get('newPassword').value?.length == 0 ||
            !control.get('newPassword').value ||
            control.get('newPassword').value !== control.get('confirmPassword').value
              ? { passwordMismatch: true }
              : null,
        ],
      }
    );

    this.changes = this.form.valueChanges.subscribe((value) => {
      const score = this.calculatePasswordStrength(value.newPassword);

      this.value = value.newPassword;

      this.score$.next(score);

      this.isValid.emit(this.form.valid);
    });
  }

  private calculatePasswordStrength(newPassword) {
    if (!newPassword) {
      newPassword = '';
    }

    let score = 0;

    if (newPassword.length > 0) {
      score = Math.max(1, checkStrength(newPassword).score);
    }
    return score;
  }

  ngOnDestroy() {
    if (this.changes) {
      this.changes.unsubscribe();
    }
  }

  writeValue(value: string): void {
    if (value !== undefined) {
      this.value = value;
    } else {
      value = '';
    }

    this.form.setValue({
      newPassword: value,
      confirmPassword: value,
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
